<!-- BaseCourseSelect.vue (쪽지/공지/과제 등에서 사용하는 과정 선택 셀렉트. 드롭다운 내 검색 지원) -->
<template>
  <div
    class="lms_select_wrap lms_course_select"
    :class="[
      { open: isOpen, disabled: disabled, error: hasError },
      size === 'sm' ? 'lms_select_sm' : '',
    ]"
  >
    <button
      ref="selectBtn"
      type="button"
      class="lms_select_trigger"
      :disabled="disabled"
      @click="toggleDropdown"
    >
      <span class="lms_select_value" :class="{ placeholder: !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <i class="mdi mdi-chevron-down lms_select_arrow"></i>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="lms_select_dropdown lms_course_dropdown"
        :style="dropdownStyle"
      >
        <div class="lms_course_search_wrap" @mousedown.prevent>
          <i class="mdi mdi-magnify lms_course_search_icon"></i>
          <input
            ref="searchInput"
            v-model="keyword"
            type="text"
            class="lms_course_search_input"
            :placeholder="searchPlaceholder"
            @mousedown.stop
          />
        </div>

        <div v-if="loading" class="lms_course_state">불러오는 중...</div>
        <div v-else-if="filtered.length === 0" class="lms_course_state">
          일치하는 과정이 없습니다.
        </div>

        <div
          v-for="opt in filtered"
          v-else
          :key="opt.courseCd"
          class="lms_select_option lms_course_option"
          :class="{ selected: selectedValue === opt.courseCd }"
          @mousedown.prevent="selectOption(opt)"
        >
          <div class="lms_course_option_title">{{ opt.sbjtNm }}</div>
          <div class="lms_course_option_meta">
            <span>{{ opt.year }}-{{ opt.term }}</span>
            <span v-if="opt.korNm">· {{ opt.korNm }}</span>
            <span v-if="opt.deptNm">· {{ opt.deptNm }}</span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onBeforeUnmount, watch } from "vue";
import { getCourseOptions } from "@/api/admin/message/MessageApi";
import type { CourseOption } from "@/types/lms/MessageType";

const selectedValue = defineModel<string | null>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | null): void;
  (e: "change", option: CourseOption | null): void;
  (e: "select", option: CourseOption | null): void;
}>();

const props = withDefaults(
  defineProps<{
    year: string;
    term?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    errorMessages?: string[];
    size?: string;
    /** 외부에서 목록을 주입하고 싶을 때. 주입 시 내부 fetch 생략 */
    items?: CourseOption[];
    /** year/term 변경 시 자동 재조회 여부 */
    autoLoad?: boolean;
  }>(),
  {
    term: "",
    placeholder: "과정을 선택해 주세요.",
    searchPlaceholder: "과목명 / 교수명 / 학과명 검색",
    disabled: false,
    errorMessages: () => [],
    size: "",
    items: undefined,
    autoLoad: true,
  }
);

const isOpen = ref(false);
const loading = ref(false);
const keyword = ref("");
const internalItems = ref<CourseOption[]>([]);
const selectBtn = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});

const hasError = computed(() => props.errorMessages.length > 0);

const options = computed<CourseOption[]>(() => props.items ?? internalItems.value);

const selectedLabel = computed(() => {
  const found = options.value.find((o) => o.courseCd === selectedValue.value);
  return found?.sbjtNm || "";
});

const filtered = computed<CourseOption[]>(() => {
  const q = keyword.value.trim().toLowerCase();
  if (!q) return options.value;
  return options.value.filter((o) => {
    return (
      o.sbjtNm?.toLowerCase().includes(q) ||
      // 교수명은 BE 가 korNm 으로 내려줌(profNm 은 미채움) → korNm 으로 매칭. profNm 은 하위호환 fallback
      o.korNm?.toLowerCase().includes(q) ||
      o.profNm?.toLowerCase().includes(q) ||
      o.deptNm?.toLowerCase().includes(q)
    );
  });
});

let loadId = 0;
const load = async () => {
  if (props.items) return;
  if (!props.year) return;
  if (loading.value) return;
  const id = ++loadId;
  loading.value = true;
  try {
    const result = await getCourseOptions({ year: props.year, term: props.term });
    if (id === loadId) {
      internalItems.value = result;
    }
  } catch {
    if (id === loadId) {
      internalItems.value = [];
    }
  } finally {
    loading.value = false;
  }
};

watch(
  () => [props.year, props.term],
  () => {
    if (props.autoLoad) load();
  },
  { immediate: true }
);

const toggleDropdown = async () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await nextTick();
    positionDropdown();
    searchInput.value?.focus();
    document.addEventListener("mousedown", onDocumentMouseDown, true);
  } else {
    document.removeEventListener("mousedown", onDocumentMouseDown, true);
  }
};

const onDocumentMouseDown = (e: MouseEvent) => {
  const target = e.target as Node | null;
  if (!target) return;
  if (selectBtn.value?.contains(target)) return;
  if (dropdownRef.value?.contains(target)) return;
  isOpen.value = false;
  keyword.value = "";
  document.removeEventListener("mousedown", onDocumentMouseDown, true);
};

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentMouseDown, true);
});

const positionDropdown = async () => {
  if (!selectBtn.value) return;
  const rect = selectBtn.value.getBoundingClientRect();
  dropdownStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${Math.max(rect.width, 280)}px`,
    zIndex: "10100",
  };
  await nextTick();
  if (dropdownRef.value) {
    const ddRect = dropdownRef.value.getBoundingClientRect();
    if (ddRect.bottom > window.innerHeight) {
      dropdownStyle.value = {
        position: "fixed",
        bottom: `${window.innerHeight - rect.top + 4}px`,
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 280)}px`,
        zIndex: "10100",
      };
    }
  }
};

const selectOption = (opt: CourseOption) => {
  selectedValue.value = opt.courseCd;
  emit("update:modelValue", opt.courseCd);
  emit("change", opt);
  emit("select", opt);
  isOpen.value = false;
  keyword.value = "";
  document.removeEventListener("mousedown", onDocumentMouseDown, true);
};

defineExpose({ reload: load });
</script>

<style scoped>
.lms_select_wrap {
  display: flex;
  position: relative;
  width: 100%;
}

.lms_select_trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--lms-outline-gray-main, #e0e0e0);
  border-radius: 4px;
  background-color: var(--lms-surface, #ffffff);
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: var(--lms-on-surface-high, rgba(0, 0, 0, 0.8));
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.lms_select_trigger:hover {
  border-color: var(--lms-outline-black, #aaabab);
}

.lms_select_wrap.open .lms_select_trigger {
  border-color: var(--lms-primary-400, #5c74f6);
  border-width: 2px;
  padding: 0 7px 0 11px;
}

.lms_select_wrap.disabled .lms_select_trigger {
  background-color: var(--lms-grey-100, #f6f7f7);
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: not-allowed;
}

.lms_select_wrap.error .lms_select_trigger {
  border-color: var(--lms-outline-error, #ff1a2d);
}

.lms_select_value.placeholder {
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}

.lms_select_arrow {
  flex-shrink: 0;
  font-size: 20px;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
  transition: transform 0.2s;
}

.lms_select_wrap.open .lms_select_arrow {
  transform: rotate(180deg);
}

.lms_select_wrap.lms_select_sm .lms_select_trigger {
  height: 32px;
  padding: 0 4px 0 10px;
  gap: 4px;
  font-size: 13px;
}
</style>

<style>
.lms_course_dropdown {
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow:
    0 1px 3px 0 rgba(145, 158, 171, 0.12),
    0 1px 1px 0 rgba(145, 157, 171, 0.14),
    0 2px 1px -1px rgba(145, 158, 171, 0.2);
  padding: 8px 0;
  max-height: 320px;
  overflow-y: auto;
}

.lms_course_search_wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 8px 6px;
  padding: 6px 10px;
  border: 1px solid var(--lms-outline-gray-main, #e0e0e0);
  border-radius: 4px;
  background: #fff;
}

.lms_course_search_wrap:focus-within {
  border-color: var(--lms-primary-400, #5c74f6);
}

.lms_course_search_icon {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.35);
  flex-shrink: 0;
}

.lms_course_search_input {
  flex: 1;
  border: none;
  outline: none;
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  background: transparent;
}

.lms_course_state {
  padding: 14px 16px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
}

.lms_course_option {
  padding: 8px 16px;
  font-family: "Pretendard", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s;
}

.lms_course_option:hover {
  background-color: #f6f7f7;
}

.lms_course_option.selected .lms_course_option_title {
  color: #5c74f6;
  font-weight: 600;
}

.lms_course_option_title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
}

.lms_course_option_meta {
  margin-top: 2px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}
</style>
